import { getCookie } from "../Helpers/cookie";
import { createElement } from "../Helpers/createElement";

declare global {
    interface Window {
        google: {
            translate: {
                TranslateElement: new (
                    options: GoogleTranslateElementOptions,
                    elementId: string
                ) => any;
            };
        };
        googleTranslateElementInit: () => void;
    }
}

interface GoogleTranslateElementOptions {
    pageLanguage: string;
    includedLanguages?: string;
    layout?: number;
    autoDisplay?: boolean;
    multilanguagePage?: boolean;
}

export const initTranslationHandlers = () => {
    const getExistingTranslation = () => {
        const languageCookie = getCookie('googtrans');

        if (languageCookie) {
            const match = languageCookie.match(/\/en\/(([a-zA-Z]*(-[a-zA-Z]*)?))/);
            return match?.[1]; 
        }
    };

    const populateTranslateWraps = () => {
        const translateWidgetTemplate = document.getElementById('translate_widget_template') as HTMLTemplateElement | null;
        const translateWraps = document.querySelectorAll('.translate-wrap') as NodeListOf<HTMLDivElement>;
        translateWraps.forEach(translateWrap => translateWidgetTemplate?.content && translateWrap.appendChild(translateWidgetTemplate.content.cloneNode(true)));
    };

    const removeDefaultOption = (translateSelector: HTMLSelectElement) => {
        if ((translateSelector.firstElementChild as HTMLOptionElement | null)?.innerText.toLowerCase() === 'select language') {
            translateSelector.removeChild(translateSelector.firstElementChild as Node);
        }
    };

    const insertDefaultOption = (translateSelector: HTMLSelectElement) => {
        if ((translateSelector.firstElementChild as HTMLOptionElement | null)?.innerText.toLowerCase() !== 'select language') {
            translateSelector.insertBefore(
                createElement('option', {
                    innerText: 'Select Language',
                    value: ''
                }),
                translateSelector.firstElementChild
            );
        }
    }

    const insertEnglishOption = (translateSelector: HTMLSelectElement) => {
        if (translateSelector.querySelector('option[value="en"]') === null) {
            const estonianOption = translateSelector.querySelector('option[value="et"]');
            if (estonianOption) {
                estonianOption.after(createElement('option', {
                    innerText: 'English',
                    value: 'en'
                }));
            }
        }
    };

    const createInContentTranslationWidget = () => {
        const pageHeading = document.querySelector('#content h1:first-of-type') as HTMLHeadingElement | null;
        const contentBlock = document.getElementById('content') as HTMLDivElement | null;

        if (pageHeading || contentBlock) {
            const remoteTranslateContainer = createElement('div', {
                id: 'remote-translate-container',
                classes: ['translate-wrap'],
                tabIndex: 0,
                style: {
                    'display': 'none' 
                }
            });

            const contentTranslateContainer = createElement('div', {
                classes: ['content-translate-container']
            });
            const buttonInputContainer = createElement('div', {
                classes: ['translate-button-input']
            });
            const translateToggle = createElement('button', {
                type: 'button',
                classes: ['translate-toggle'],
                ariaControls: 'remote-translate-container',
                ariaExpanded: 'false',
                handlers: [{
                    type: 'focusout',
                    listener: (event: FocusEvent) => {
                        if (!event.relatedTarget && !buttonInputContainer.contains(event.relatedTarget)) {
                            buttonInputContainer.classList.remove('active');
                            translateToggle.ariaExpanded = 'false';
                            remoteTranslateContainer.style.display = 'none';
                        }
                    }
                }],
                children: [
                    createElement('i', {
                        classes: ['fas', 'fa-globe'],
                        ariaHidden: 'true'
                    }),
                    createElement('span', {
                        innerText: 'Translate This Page'
                    })
                ]
            });

            buttonInputContainer.appendChild(translateToggle);
            buttonInputContainer.appendChild(remoteTranslateContainer);
            contentTranslateContainer.appendChild(buttonInputContainer);

            pageHeading && pageHeading.nextElementSibling ?
                pageHeading.parentElement?.insertBefore(contentTranslateContainer, pageHeading.nextElementSibling) :
                    pageHeading ?
                        pageHeading.parentElement?.append(contentTranslateContainer) :
                        contentBlock?.insertBefore(contentTranslateContainer, contentBlock.firstElementChild);
        }
    };

    const initTranslateSelectorsSync = () => {
        const remoteTranslateSelectors = document.querySelectorAll('.remote-translate-element select') as NodeListOf<HTMLSelectElement>;
        const nativeGoogleTranslateSelector = document.querySelector('#google_translate_element select') as HTMLSelectElement | null;
        const existingTranslation = getExistingTranslation();

        remoteTranslateSelectors.forEach(translateSelector => {
            if (existingTranslation && existingTranslation !== 'en') {
                removeDefaultOption(translateSelector);
                insertEnglishOption(translateSelector);
                translateSelector.value = existingTranslation;
            }

            translateSelector.addEventListener('change', event => {
                removeDefaultOption(translateSelector);
                insertEnglishOption(translateSelector);

                if ((event.target as HTMLSelectElement | null)?.value) {
                    remoteTranslateSelectors.forEach(remoteTranslateSelector => remoteTranslateSelector.value = (event.target as HTMLSelectElement).value);

                    if (nativeGoogleTranslateSelector) {
                        nativeGoogleTranslateSelector.value = (event.target as HTMLSelectElement).value;
                        nativeGoogleTranslateSelector.dispatchEvent(new Event('change'));
                    }
                }
            });
        });

        nativeGoogleTranslateSelector?.addEventListener('change', event => 
            Array.prototype.forEach.call(remoteTranslateSelectors, translateSelector => {
                if ((event.target as HTMLSelectElement)?.value === '') {
                    insertDefaultOption(translateSelector);
                }

                translateSelector.value = (event.target as HTMLSelectElement).value;
            })
        );

        new MutationObserver(mutationList =>
            mutationList.forEach(mutation => {
                if (mutation.type === 'attributes' &&
                    (!(mutation.target as HTMLElement).classList.contains('translated-rtl') && !(mutation.target as HTMLElement).classList.contains('translated-ltr'))) {
                    Array.prototype.forEach.call(remoteTranslateSelectors, translateSelector => {
                        insertDefaultOption(translateSelector);
                        translateSelector.value = '';
                    });
                }
            })
        )
        .observe(document.documentElement, { attributeFilter: ['class'] });

        const translateTitleObserver = new MutationObserver(mutatationList =>
            mutatationList.forEach(mutation => {
                for (const node of mutation.addedNodes) {
                    const container = node as HTMLElement;

                    if (container.tagName === 'DIV' && container.classList.contains('skiptranslate') &&
                        container.firstElementChild?.tagName === 'IFRAME' && container.firstElementChild.classList.contains('skiptranslate')) {
                        const element = container.firstElementChild as HTMLIFrameElement;
                        if (!element.title) {
                            element.title = 'Language Translate Widget';
                        }
                        
                        translateTitleObserver.disconnect();
                    }
                }
            })
        );

        translateTitleObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    const initTranslateTogglesHandler = () => {
        const translateToggles = document.querySelectorAll('.translate-toggle') as NodeListOf<HTMLButtonElement>;
        translateToggles.forEach(toggle => {
            if (toggle.ariaControlsElements?.[0]) {
                toggle.addEventListener('click', () => {
                    if (toggle.ariaExpanded === 'true') {
                        toggle.ariaExpanded = 'false';
                        if (toggle.ariaControlsElements?.[0]) (toggle.ariaControlsElements[0] as HTMLDivElement).style.display = 'none';
                        if (toggle.parentElement?.classList.contains('translate-button-input')) toggle.parentElement.classList.remove('active');
                    } else {
                        toggle.ariaExpanded = 'true';
                        if (toggle.ariaControlsElements?.[0]) (toggle.ariaControlsElements[0] as HTMLDivElement).style.removeProperty('display');
                        if (toggle.parentElement?.classList.contains('translate-button-input')) toggle.parentElement.classList.add('active');
                    }
                });

                if (toggle.classList.contains('desktop-translate-toggle')) {
                    const pageHeader = document.querySelector('header .header-top') ?
                        document.querySelector('header .header-top') as HTMLElement | null :
                        document.querySelector('header') as HTMLElement | null;

                    if (pageHeader) {
                        pageHeader.addEventListener('mouseleave', () => {
                            toggle.ariaExpanded = 'false';
                            if (toggle.ariaControlsElements?.[0]) (toggle.ariaControlsElements[0] as HTMLDivElement).style.display = 'none';
                            if (toggle.parentElement?.classList.contains('translate-button-input')) toggle.parentElement.classList.remove('active');
                        });

                        pageHeader.addEventListener('focusout', event => {
                            if (!event.relatedTarget || !pageHeader.contains(event.relatedTarget as Node)) {
                                toggle.ariaExpanded = 'false';
                                if (toggle.ariaControlsElements?.[0]) (toggle.ariaControlsElements[0] as HTMLDivElement).style.display = 'none';
                                if (toggle.parentElement?.classList.contains('translate-button-input')) toggle.parentElement.classList.remove('active');
                            }
                        });
                    }
                }
            }
        });
    };

    window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement({ pageLanguage: 'en' }, 'google_translate_element');

        if (document.body.classList.contains('t-widget')) {
            createInContentTranslationWidget();
        }

        populateTranslateWraps();
        initTranslateSelectorsSync();
        initTranslateTogglesHandler();        
    };
};
